import React, { useEffect, useMemo, useState } from "react";
import { SparklesIcon } from "@heroicons/react/24/solid";
import boltGradientSvg from "~/assets/bolt-gradient.svg";
import { Button } from "@mindstonehq/ui";
import Markdown from "marked-react";
import { InlineTriviaExtension } from "~/typings/annotate/annotation";
import { LoadingIcon } from "~/components/icons";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

const messageNoObjectives: MessageDescriptor[] = [
  msg`You added proof of your growing expertise to your skill profile.`,
  msg`You levelled up your skill profile with bonus evidence points.`,
  msg`Your profile has been updated with new evidence of your expertise.`,
];
const messageObjectives: MessageDescriptor[] = [
  msg`Active learner alert! You aced your unique trivia and are cruising towards **{NAME}**. Let's finish this 💪.`,
  msg`You nailed this 1-of-1 trivia and got bonus evidence points to unlock the certificate in your **{NAME}** level.`,
  msg`That quiz was picked just for you and you owned it! Your skill profile is now updated with proof of your growing **{NAME}** expertise.`,
];

export function ExtensionTrivia({
  extension,
}: React.PropsWithoutRef<{
  extension: InlineTriviaExtension;
}>) {
  const { t, i18n } = useLingui();
  const [submittingAnswerID, setSubmittingAnswerID] = useState("");
  const [result, setResult] = useState(extension.result);
  const [reasonMessage, setReasonMessage] = useState("");
  const submitAnswer = useMemo(
    () => (answerID: string) => {
      setSubmittingAnswerID(answerID);
      extension
        .submit(extension.question.id, answerID)
        .then((res) => {
          setResult(res);
        })
        .finally(() => setSubmittingAnswerID(""));
    },
    [extension],
  );

  useEffect(() => {
    setResult(result);
  }, [extension.result]);

  useEffect(() => {
    if (!result) {
      setReasonMessage("");
      return;
    }
    switch (result.result) {
      case "CORRECT":
        if (result.perObjective.length) {
          const index = Math.floor(Math.random() * messageObjectives.length);
          setReasonMessage(
            i18n._({
              ...messageObjectives[index],
              values: { NAME: result.perObjective[0].objective.name },
            }),
          );
        } else {
          const index = Math.floor(Math.random() * messageNoObjectives.length);
          setReasonMessage(t(messageNoObjectives[index]));
        }
        return;
      case "WRONG":
        const selectedAnswer = result.answers?.find((x) => x.isUserAnswer);
        if (!selectedAnswer) {
          setReasonMessage(""); //just in case
          return;
        }
        setReasonMessage(selectedAnswer.explanation);
        return;
    }
    return;
  }, [result]);

  const hasResult = !!result;
  return (
    <div className="flex flex-row space-x-[6px] my-2 not-prose">
      <SparklesIcon className="text-primary h-4 w-4 shrink-0" />
      {!hasResult && (
        <div className="flex flex-col border-l-4 border-ai-hover pl-3 space-y-2">
          <span className={"text-foreground"}>
            {extension.question.question}
          </span>
          <div className="flex flex-wrap flex-row gap-2">
            {extension.question.answers.map((x) => (
              <Button
                key={x.id}
                variant="outline"
                size="sm"
                className={"whitespace-normal h-auto min-h-[2.25rem]"}
                disabled={!!submittingAnswerID}
                onClick={() => submitAnswer(x.id)}
              >
                {submittingAnswerID === x.id && (
                  <LoadingIcon className={"animate-spin h-5 w-5"} />
                )}
                {x.answer}
              </Button>
            ))}
          </div>
        </div>
      )}
      {hasResult && (
        <div className="flex flex-col border-l-4 border-ai-hover pl-3">
          <div className="flex items-center gap-1">
            {result.result === "CORRECT" && (
              <>
                <span>
                  <Trans>Correct! You just earned {result.earnedTriviaPoints} EP</Trans>
                </span>
                <img
                  src={boltGradientSvg}
                  alt={t(msg`points`)}
                  className="w-3 h-3"
                />
              </>
            )}
            {result.result === "WRONG" && t(msg`That was tricky! Let's review 💡`)}
          </div>
          <span className={"text-sm text-placeholder-foreground"}>
            <Markdown isInline={true}>{reasonMessage}</Markdown>
          </span>
        </div>
      )}
    </div>
  );
}
